import api from '../config';

/**
 * Authentication API Service
 * 
 * Contains all authentication related API calls to the WDTP backend.
 * Base URL: https://beta-auth.wdtp.io/api
 * All authentication endpoints should include the "/auth/" prefix
 */
const authService = {
  /**
   * Register a new user
   * 
   * @param {Object} userData - The user registration data
   * @param {string} userData.username - The desired username
   * @param {string} userData.email - A valid email address
   * @param {string} userData.password - Password (min 8 chars, including uppercase, lowercase, digit, special char)
   * @returns {Promise} Promise with the registration response
   */
  register: async (userData) => {
    console.log('Register API called with data:', {
      ...userData,
      password: userData.password ? '******' : undefined // Don't log actual password
    });
    try {
      const response = await api.post('/auth/register', userData);
      console.log('Register API response:', response.data);
      return response;
    } catch (error) {
      console.error('Register API error:', error);
      if (error.response) {
        console.error('Error status:', error.response.status);
        console.error('Error data:', error.response.data);
      }
      throw error;
    }
  },

  /**
   * Sign in a user
   * 
   * @param {string} email - The user's registered email
   * @param {string} password - The user's password
   * @returns {Promise} Promise with JWT token if credentials are valid
   */
  signin: async (email, password) => {
    console.log('Sign in API called for email:', email);
    try {
      const response = await api.post('/auth/signin', { email, password });
      
      // Check for different token formats including deep nesting
      const hasStandardTokens = response.data.accessToken && response.data.refreshToken;
      const hasSingleToken = response.data.token || 
                         (response.data.data && response.data.data.token) ||
                         (response.data.data && response.data.data.data && response.data.data.data.token);
      const hasAnyToken = hasStandardTokens || hasSingleToken;
      
      // Find the actual token value wherever it is
      let tokenValue = null;
      if (response.data.token) {
        tokenValue = response.data.token;
      } else if (response.data.data && response.data.data.token) {
        tokenValue = response.data.data.token;
      } else if (response.data.data && response.data.data.data && response.data.data.data.token) {
        tokenValue = response.data.data.data.token;
      }
      
      // Log the response format with detailed structure information
      console.log(`Sign in response ${hasAnyToken ? 'WITH TOKEN' : 'without tokens'}:`, {
        status: response.status,
        tokenFormat: hasStandardTokens ? 'standard (accessToken+refreshToken)' : 
                     hasSingleToken ? 'single token' : 'no tokens',
        tokenLocation: tokenValue ? (
          response.data.token ? 'data.token' : 
          (response.data.data && response.data.data.token) ? 'data.data.token' :
          'data.data.data.token'
        ) : 'no token found',
        tokenPreview: tokenValue ? `${tokenValue.substring(0, 15)}...` : null,
        hasAccessToken: !!response.data.accessToken,
        hasRefreshToken: !!response.data.refreshToken,
        hasSingleToken: !!hasSingleToken,
        hasUser: !!response.data.user,
        message: response.data.message,
        success: response.data.success,
        responseDataKeys: Object.keys(response.data),
        nestedDataKeys: response.data.data ? Object.keys(response.data.data) : 'no nested data'
      });
      
      // If we have tokens, this should be a successful login
      if (hasAnyToken) {
        console.log('LOGIN SUCCESSFUL - token found in response');
        if (hasSingleToken && !hasStandardTokens) {
          console.log('Using single token format instead of separate access/refresh tokens');
        }
      } else if (response.data.message && response.data.message.includes('Verification')) {
        console.log('VERIFICATION REQUIRED - message indicates verification needed');
      } else if (response.data.success === true) {
        console.log('SUCCESS WITHOUT TOKEN - unusual response format');
      }
      
      return response;
    } catch (error) {
      console.error('Sign in API error:', error);
      if (error.response) {
        console.error('Error status:', error.response.status);
        console.error('Error data:', error.response.data);
        
        // Check if error response contains tokens
        const hasStandardTokens = error.response.data.accessToken && error.response.data.refreshToken;
        const hasSingleToken = error.response.data.token || 
                              (error.response.data.data && error.response.data.data.token);
        
        if (hasStandardTokens || hasSingleToken) {
          console.warn('WARNING: Tokens found in error response. This is unusual.');
        }
      }
      throw error;
    }
  },

  /**
   * Verify user's email using verification code
   * 
   * @param {string} email - The user's registered email
   * @param {string} code - The verification code sent to email
   * @returns {Promise} Promise with verification response
   */
  verifyEmail: async (email, code) => {
    console.log('Verify Email API called with:', { email, code });
    try {
      const response = await api.get(`/auth/verify-email?email=${encodeURIComponent(email)}&code=${encodeURIComponent(code)}`);
      console.log('Verify Email API response:', response.data);
      return response;
    } catch (error) {
      console.error('Verify Email API error:', error);
      if (error.response) {
        console.error('Error status:', error.response.status);
        console.error('Error data:', error.response.data);
      }
      throw error;
    }
  },

  /**
   * Request password reset (forgot password)
   * 
   * @param {string} email - The user's registered email
   * @returns {Promise} Promise with response
   */
  forgotPassword: (email) => {
    return api.post('/auth/forgot-password', { email });
  },

  /**
   * Reset password using reset code
   * 
   * @param {string} email - The user's registered email
   * @param {string|number} code - The verification code/OTP received by email
   * @param {string} password - The new password
   * @returns {Promise} Promise with reset response
   */
  resetPassword: async (email, code, password) => {
    console.log('Reset Password API called with all three required fields:', { 
      email, 
      code, 
      passwordLength: password ? password.length : 0 
    });
    
    // Ensure code is treated as string in case it comes as a number
    const resetCode = String(code);
    
    // The API endpoint expects all three parameters in a single call:
    // - email: The user's email
    // - code: The verification code/OTP
    // - password: The new password
    try {
      const response = await api.post('/auth/reset-password', { 
        email, 
        code: resetCode, 
        password 
      });
      console.log('Reset Password API response:', {
        status: response.status,
        data: response.data
      });
      return response;
    } catch (error) {
      console.error('Reset Password API error:', error);
      if (error.response) {
        console.error('Error status:', error.response.status);
        console.error('Error data:', error.response.data);
      }
      throw error;
    }
  },

  /**
   * Get user profile
   * 
   * @returns {Promise} Promise with user profile data
   */
  getUserProfile: () => {
    return api.get('/auth/me');
  },

  /**
   * Logout the user
   * 
   * @returns {Promise} Promise with logout response
   */
  logout: async () => {
    console.log('Calling logout API endpoint');
    try {
      const response = await api.post('/auth/logout');
      console.log('Logout API response:', response.status);
      return response;
    } catch (error) {
      // If we get 401, this is expected during logout (token might already be invalid)
      if (error.response && error.response.status === 401) {
        console.log('Logout API returned 401 - token likely already expired or invalid');
        // Return a standardized response for expired token that can be handled by the caller
        return { 
          status: 'already-logged-out',
          message: 'Token was invalid or expired, local logout performed',
          statusCode: 401
        };
      }
      
      // If we get 404, the endpoint might not exist in this API version
      if (error.response && error.response.status === 404) {
        console.log('Logout API returned 404 - endpoint might not exist in this API version');
        return {
          status: 'no-endpoint',
          message: 'Logout endpoint not available, local logout performed',
          statusCode: 404
        };
      }
      
      // For other errors, log and return a standardized response
      console.error('Logout API error:', error.message || 'Unknown error');
      if (error.response) {
        console.error('Error status:', error.response.status);
        console.error('Error data:', error.response.data);
        
        // Return a standardized error response that can be handled by the caller
        return {
          status: 'error',
          message: `Server error during logout (${error.response.status})`,
          statusCode: error.response.status,
          error: error
        };
      }
      
      // Network or other errors without response
      return {
        status: 'error',
        message: 'Network error during logout',
        error: error
      };
    }
  },

  /**
   * Get available service endpoints
   * 
   * @returns {Promise} Promise with service endpoints data
   */
  getEndpoints: async () => {
    console.log('Getting service endpoints');
    try {
      const response = await api.get('/auth/get-endpoints');
      console.log('Service endpoints response:', response.data);
      return response;
    } catch (error) {
      console.error('Service endpoints API error:', error);
      if (error.response) {
        console.error('Error status:', error.response.status);
        console.error('Error data:', error.response.data);
      }
      throw error;
    }
  },
};

export default authService; 